import { App } from '@capacitor/app';
import { Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

type Logger = Pick<typeof console, 'debug' | 'error' | 'warn'>;

export interface PdfOpenPayload {
  origin: 'capacitor';
  webViewSrc: string;
  fileName?: string;
  data?: Uint8Array;
  documentIdentifier?: string;
  /**
   * Invoke to release any memory or object URLs allocated for this payload.
   */
  cleanup?: () => void;
  /**
   * Original file path when available (for diagnostics or re-processing).
   */
  filePath?: string;
}

interface RegisterPdfOpenHandlersOptions {
  onPdfOpen: (payload: PdfOpenPayload) => void;
  onError?: (error: unknown) => void;
  logger?: Logger;
}

type CleanupFn = () => void;

interface ParsedPdfRequest {
  kind: 'path' | 'base64';
  value: string;
  fileName?: string;
}

/**
 * Registers listeners for platform-specific PDF open events.
 * Returns a cleanup function that removes all registered listeners.
 */
export function registerPdfOpenHandlers(options: RegisterPdfOpenHandlersOptions): CleanupFn {
  const { onPdfOpen, onError, logger = console } = options;
  const cleanups: CleanupFn[] = [];

  const handleCapacitorUrlOpen = async (url: string) => {
    logger.debug?.('[pdfOpenHandlers] Received appUrlOpen event', { url });
    try {
      const parsed = parsePdfRequest(url);
      if (!parsed) {
        logger.debug?.('[pdfOpenHandlers] Ignoring appUrlOpen without PDF payload', { url });
        return;
      }

      if (parsed.kind === 'base64') {
        const data = base64ToUint8Array(parsed.value);
        const { objectUrl, cleanup } = createObjectUrlFromData(data);
        onPdfOpen({
          origin: 'capacitor',
          webViewSrc: objectUrl,
          cleanup,
          data,
          fileName: parsed.fileName,
          documentIdentifier: deriveDocumentIdentifier({
            fileName: parsed.fileName,
          }),
        });
        return;
      }

      const filePath = normalizeFilePath(parsed.value);
      const fallbackWebViewSrc = createWebViewSrcFromPath(filePath);
      let data: Uint8Array | undefined;
      let objectUrl: string | undefined;
      let cleanup: CleanupFn | undefined;

      try {
        const filesystemResult = await readPdfFromFilesystem(filePath, logger);
        if (filesystemResult) {
          data = filesystemResult.data;
          objectUrl = filesystemResult.objectUrl;
          cleanup = filesystemResult.cleanup;
        }
      } catch (readError) {
        logger.warn?.('[pdfOpenHandlers] Falling back to direct file URL for PDF', { filePath, readError });
      }

      const webViewSrc = objectUrl ?? fallbackWebViewSrc;
      if (!webViewSrc) {
        throw new Error('Unable to resolve PDF source URL');
      }

      onPdfOpen({
        origin: 'capacitor',
        webViewSrc,
        cleanup,
        data,
        fileName: parsed.fileName ?? extractFileName(filePath),
        filePath,
        documentIdentifier: deriveDocumentIdentifier({
          filePath,
          fileName: parsed.fileName,
        }),
      });
    } catch (error) {
      logger.error?.('[pdfOpenHandlers] Failed handling Capacitor PDF open', error);
      onError?.(error);
    }
  };

  if (Capacitor.isNativePlatform()) {
    const listenerPromise = App.addListener('appUrlOpen', (event) => {
      if (event?.url) {
        void handleCapacitorUrlOpen(event.url);
      }
    });

    cleanups.push(() => {
      listenerPromise
        .then((handle) => handle.remove())
        .catch((err) => logger.warn?.('[pdfOpenHandlers] Failed removing appUrlOpen listener', err));
    });
  }

  return () => {
    for (const cleanup of cleanups) {
      try {
        cleanup();
      } catch (error) {
        logger.warn?.('[pdfOpenHandlers] Cleanup failed', error);
      }
    }
  };
}

interface ReadFilesystemResult {
  data: Uint8Array;
  objectUrl: string;
  cleanup: CleanupFn;
}

async function readPdfFromFilesystem(path: string, logger: Logger): Promise<ReadFilesystemResult | null> {
  const cleanupFns: CleanupFn[] = [];

  const candidatePaths = Array.from(new Set(generateFilesystemPathCandidates(path)));
  let lastError: unknown;

  for (const candidate of candidatePaths) {
    try {
      const result = await Filesystem.readFile({ path: candidate });
      const data = await toUint8Array(result.data);
      const { objectUrl, cleanup } = createObjectUrlFromData(data);
      cleanupFns.push(cleanup);

      return {
        data,
        objectUrl,
        cleanup: () => {
          for (const fn of cleanupFns.reverse()) {
            fn();
          }
        },
      };
    } catch (error) {
      lastError = error;
      logger.debug?.('[pdfOpenHandlers] Filesystem.readFile attempt failed', { candidate, error });
    }
  }

  logger.warn?.('[pdfOpenHandlers] Filesystem.readFile failed for all candidates, attempting fetch fallback', {
    path,
    lastError,
  });

  if (path.startsWith('file://') || path.startsWith('capacitor://')) {
    try {
      const response = await fetch(path);
      const buffer = await response.arrayBuffer();
      const data = new Uint8Array(buffer);
      const { objectUrl, cleanup } = createObjectUrlFromData(data);
      cleanupFns.push(cleanup);
      return {
        data,
        objectUrl,
        cleanup: () => {
          for (const fn of cleanupFns.reverse()) {
            fn();
          }
        },
      };
    } catch (fetchError) {
      logger.error?.('[pdfOpenHandlers] Fetch fallback failed for PDF path', { path, fetchError });
      throw fetchError;
    }
  }

  if (lastError) {
    throw lastError;
  }

  return null;
}

function parsePdfRequest(url: string): ParsedPdfRequest | null {
  try {
    const parsed = new URL(url);
    const fileName = parsed.searchParams.get('fileName') ?? parsed.searchParams.get('name') ?? undefined;
    const base64Param = parsed.searchParams.get('base64') ?? parsed.searchParams.get('data');
    if (base64Param) {
      return {
        kind: 'base64',
        value: normalizeBase64(base64Param),
        fileName,
      };
    }

    const pathParam = parsed.searchParams.get('path')
      ?? parsed.searchParams.get('filePath')
      ?? parsed.searchParams.get('uri');

    if (pathParam) {
      return {
        kind: 'path',
        value: decodeURIComponent(pathParam),
        fileName,
      };
    }

    if (parsed.protocol === 'file:' || parsed.protocol === 'capacitor:' || parsed.protocol.startsWith('http')) {
      return {
        kind: 'path',
        value: decodeURIComponent(parsed.href),
        fileName: fileName ?? extractFileName(parsed.pathname),
      };
    }

    if (parsed.pathname && parsed.pathname !== '/') {
      return {
        kind: 'path',
        value: decodeURIComponent(parsed.pathname),
        fileName,
      };
    }
  } catch {
    if (url.startsWith('file://') || url.startsWith('capacitor://')) {
      return {
        kind: 'path',
        value: decodeURIComponent(url),
        fileName: extractFileName(url),
      };
    }
  }

  return null;
}

function createObjectUrlFromData(data: Uint8Array) {
  const blob = new Blob([data as BlobPart], { type: 'application/pdf' });
  const objectUrl = URL.createObjectURL(blob);
  return {
    objectUrl,
    cleanup: () => URL.revokeObjectURL(objectUrl),
  };
}

async function toUint8Array(data: string | Blob): Promise<Uint8Array> {
  if (typeof data === 'string') {
    return base64ToUint8Array(normalizeBase64(data));
  }

  const buffer = await data.arrayBuffer();
  return new Uint8Array(buffer);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const cleaned = normalizeBase64(base64);
  const globalAtob =
    typeof globalThis !== 'undefined' ? (globalThis as { atob?: typeof atob }).atob : undefined;
  const decoder = typeof atob === 'function' ? atob : typeof globalAtob === 'function' ? globalAtob : null;

  if (!decoder) {
    throw new Error('Base64 decoder not available in this environment');
  }

  const binaryString = decoder(cleaned);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function normalizeBase64(value: string) {
  return value.replace(/[\n\r\s]/g, '').replace(/-/g, '+').replace(/_/g, '/');
}

function normalizeFilePath(path: string) {
  const decoded = decodeURIComponent(path);
  if (decoded.startsWith('file://') || decoded.startsWith('capacitor://')) {
    return decoded;
  }
  return decoded.replace(/^\/+/, '/');
}

function extractFileName(path: string | null | undefined) {
  if (!path) return undefined;
  const normalized = path.split(/[?#]/)[0];
  const segments = normalized.split(/[\\/]/);
  return segments[segments.length - 1] || undefined;
}

function generateFilesystemPathCandidates(path: string) {
  const candidates: string[] = [];

  const strippedFile = path.startsWith('file://') ? path.replace(/^file:\/\//, '') : null;
  const strippedCapacitor = path.startsWith('capacitor://')
    ? path.replace(/^capacitor:\/\/[^/]+/, '')
    : null;

  if (strippedFile) {
    candidates.push(strippedFile);
  }

  if (strippedCapacitor) {
    candidates.push(strippedCapacitor.startsWith('/') ? strippedCapacitor.slice(1) : strippedCapacitor);
  }

  candidates.push(path);

  return candidates.filter(Boolean);
}

function createWebViewSrcFromPath(path: string | undefined) {
  if (!path) return undefined;
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  if (path.startsWith('capacitor://')) {
    return path;
  }
  if (path.startsWith('file://')) {
    return Capacitor.convertFileSrc(path);
  }
  if (path.startsWith('/')) {
    return Capacitor.convertFileSrc(`file://${path}`);
  }
  return path;
}

function deriveDocumentIdentifier({
  filePath,
  fileName,
}: {
  filePath?: string;
  fileName?: string;
}) {
  const candidates = [
    extractLastPathSegment(filePath),
    extractFileName(filePath),
    fileName,
    stripPdfExtension(extractLastPathSegment(filePath)),
    stripPdfExtension(fileName),
  ].filter(Boolean);

  return candidates[0];
}

function stripPdfExtension(value?: string) {
  if (!value) return undefined;
  return value.toLowerCase().endsWith('.pdf') ? value.slice(0, -4) : value;
}

function extractLastPathSegment(path?: string) {
  if (!path) return undefined;
  const withoutQuery = path.split(/[?#]/)[0];
  const segments = withoutQuery.split('/');
  return segments[segments.length - 1] || undefined;
}

